import mapboxgl from 'mapbox-gl'
import { RefObject, useEffect, useRef } from 'react'
import type { MapMarker } from '../index'
import { createMarkerHtml } from '../utils/markerHtml'
import styles from '../Map.module.scss'

interface UseMapMarkersOptions {
  mapRef: RefObject<mapboxgl.Map | null>
  isMapReady: boolean
  markers: MapMarker[]
  userLocation: [number, number] | null
  showUserLocation: boolean
  showMarkerBadges: boolean
}

export const useMapMarkers = ({
  mapRef,
  isMapReady,
  markers,
  userLocation,
  showUserLocation,
  showMarkerBadges,
}: UseMapMarkersOptions) => {
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null)
  const hasUserInteracted = useRef(false)

  // Track user interaction with the map (zoom/pan)
  useEffect(() => {
    const map = mapRef.current
    if (!isMapReady || !map) return

    const handleInteraction = () => {
      hasUserInteracted.current = true
    }

    map.on('zoomstart', handleInteraction)
    map.on('dragstart', handleInteraction)

    return () => {
      map.off('zoomstart', handleInteraction)
      map.off('dragstart', handleInteraction)
    }
  }, [mapRef, isMapReady])

  // Add user location marker
  useEffect(() => {
    const map = mapRef.current
    if (!isMapReady || !map || !userLocation || !showUserLocation) return

    // Remove existing user marker
    userMarkerRef.current?.remove()

    const userMarkerEl = document.createElement('div')
    userMarkerEl.className = styles.userMarker

    userMarkerRef.current = new mapboxgl.Marker({ element: userMarkerEl })
      .setLngLat(userLocation)
      .addTo(map)

    return () => {
      userMarkerRef.current?.remove()
      userMarkerRef.current = null
    }
  }, [mapRef, isMapReady, userLocation, showUserLocation])

  // Add route markers
  useEffect(() => {
    const map = mapRef.current
    if (!isMapReady || !map) return

    // Remove existing markers
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    // Add new markers
    markers.forEach((markerData) => {
      const el = document.createElement('div')
      el.className = styles.marker
      el.innerHTML = createMarkerHtml(
        markerData.stepNumber,
        markerData.status,
        showMarkerBadges
      )

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([markerData.lng, markerData.lat])
        .addTo(map)

      markersRef.current.push(marker)
    })

    return () => {
      markersRef.current.forEach((marker) => marker.remove())
      markersRef.current = []
    }
  }, [mapRef, isMapReady, markers, showMarkerBadges])

  // Fit bounds to show all markers and user location (only if user hasn't interacted)
  useEffect(() => {
    const map = mapRef.current
    if (!isMapReady || !map || markers.length === 0) return

    // Only fit bounds if user hasn't manually zoomed/panned
    if (hasUserInteracted.current) return

    const bounds = new mapboxgl.LngLatBounds()
    markers.forEach((m) => bounds.extend([m.lng, m.lat]))
    if (userLocation) bounds.extend(userLocation)
    map.fitBounds(bounds, { padding: 50, maxZoom: 14, animate: false })
  }, [mapRef, isMapReady, markers, userLocation])
}
